import {useEffect, useLayoutEffect, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../helpers'
import {IHeader, SettingsPageSetup, useLayout} from '../../core'
import {Topbar} from './Topbar'
import {AnnouncementCarousel} from '../../../../app/modules/common/components/AnnouncementCarousel'
import {useAuth} from '../../../../app/modules/auth'
import {ROUTES, HIDDEN_TOP_BAR_ROUTERS} from '../../../constants'
import UseRoutesPermission from '../../../hooks/UseRoutesPermission'
import {HeaderNavigation} from './HeaderNavigation'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import clsx from 'clsx'
import {DefaultTitle} from './page-title/DefaultTitle'

const calculateStickyOffset = (header: IHeader): string => {
  if (header.fixed.desktop && header.fixed.tabletAndMobile) {
    return '{default: "200px", lg: "300px"}'
  }

  if (header.fixed.desktop && !header.fixed.tabletAndMobile) {
    return '{lg: "300px"}'
  }

  if (header.fixed.tabletAndMobile && !header.fixed.desktop) {
    return '{default: "200px", lg: false}'
  }

  return ''
}

const calculateShowSticky = (header: IHeader): boolean => {
  return (
    (header.fixed.desktop && header.fixed.tabletAndMobile) ||
    (header.fixed.desktop && !header.fixed.tabletAndMobile) ||
    (header.fixed.tabletAndMobile && !header.fixed.desktop)
  )
}

export function HeaderWrapper() {
  const {width} = useWindowDimensions()
  const {config, attributes} = useLayout()
  const {header} = config
  const [stickyOffset, setStickyOffset] = useState<string>(calculateStickyOffset(header))
  const [showSticky, setShowSticky] = useState<boolean>(calculateShowSticky(header))
  const {currentUser} = useAuth()
  const {routes, firstPage} = UseRoutesPermission()
  const [hideTopBar, setHideTopBar] = useState<boolean>(false)
  const {pathname} = useLocation()
  const logoTo =
    currentUser && routes.ORDERS_VIEW.hasPermission
      ? ROUTES.ORDERS.BASE
      : firstPage?.route ?? ROUTES.SUPPORT.BASE

  const isSettingsPage =
    pathname === ROUTES.SETTINGS.BASE || pathname.startsWith(`${ROUTES.SETTINGS.BASE}/`)
  const shouldShowSettingsMenu = isSettingsPage && width >= 1400

  useLayoutEffect(() => {
    SettingsPageSetup(pathname)
    const onResize = () => SettingsPageSetup(pathname)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [pathname])

  useEffect(() => {
    setStickyOffset(calculateStickyOffset(header))
    setShowSticky(calculateShowSticky(header))
    if (HIDDEN_TOP_BAR_ROUTERS.includes(pathname)) {
      setHideTopBar(true)
    }
  }, [header, pathname])

  return (
    <div
      id='gori_header'
      className='header-wrapper'
      {...attributes.headerMenu}
      data-kt-sticky={showSticky ? 'true' : 'false'}
      data-kt-sticky-name='header'
      data-kt-sticky-offset={stickyOffset}
    >
      <AnnouncementCarousel eligible={true} />
      <div
        className='m-0 d-flex flex-stack flex flex-nowrap gap-2'
        id='gori_header_container'
        style={{marginTop: '20px'}}
      >
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div className='d-flex align-items-center me-2 header-nav'>
            {currentUser && (
              <div
                className='btn btn-icon btn-active-icon-primary header-toggle-button'
                id='gori_aside_toggle'
              >
                <KTSVG path='/media/gori/header-icon/toggle.svg' className='svg-icon-1 mt-1' />
              </div>
            )}
            {/* Logo */}
            <Link
              to={logoTo}
              className={clsx('d-flex align-items-center logo-link', {
                'd-none': shouldShowSettingsMenu,
              })}
            >
              <KTSVG path='/media/logos/shipbae-logo-black.svg' svgClassName='' small={false} />
            </Link>
            <HeaderNavigation />
            <DefaultTitle />
          </div>
        </div>

        {!hideTopBar && <Topbar />}
      </div>
    </div>
  )
}
